import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../redux/store/hooks';
import { useCreateInvoice, useRecordPayment, useUnbilledCharges } from '../../services/httpServices/queries';
import { billingService } from '../../services/httpServices/billingService';
import { printInvoicePdf } from '../../utils/printDocument';
import { InvoicePaymentCard } from './InvoicePaymentCard';
import { PatientVisitSelector } from '../../components/ui/patient-visit-selector';
import { PageHelp } from '../../components/ui/PageHelp';
import { InfoHint } from '../../components/ui/InfoHint';
import { DateTimePicker } from '../../components/ui/DatePicker';
import { usePermissions } from '../../hooks/usePermissions';
import { useHospitalTimezone } from '../../hooks/useHospitalTimezone';
import {
  datetimeLocalToISOInTimeZone,
  toDatetimeLocalValueInTimeZone,
} from '../../lib/timezones';
import { formatQuantity } from '../../utils/servicePricing';
import {
  DiscountState,
  DiscountType,
  money,
  resolveDiscountAmount,
  round2,
  sanitizeDecimalInput,
} from './invoice-discount-utils';
import { toast } from 'sonner';
import {
  AlertCircle,
  ArrowLeft,
  BadgePercent,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Globe,
  Info,
  ListChecks,
  Loader2,
  Plus,
  Printer,
  Receipt,
  StickyNote,
  Tag,
  Wallet,
} from 'lucide-react';

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary';

export function InvoiceCreate() {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const createInvoice = useCreateInvoice();
  const recordPayment = useRecordPayment();

  const [patientId, setPatientId] = useState(searchParams.get('patientId') || '');
  const [visitId, setVisitId] = useState(searchParams.get('visitId') || '');
  const [selectedChargeIds, setSelectedChargeIds] = useState<string[]>([]);
  const [discounts, setDiscounts] = useState<Record<string, DiscountState>>({});
  const [invoiceDiscount, setInvoiceDiscount] = useState<DiscountState>({ value: '', type: 'PERCENT' });
  const [notes, setNotes] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [createdInvoice, setCreatedInvoice] = useState<any>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [printBusy, setPrintBusy] = useState(false);

  // ── Backdating ──
  // An invoice's business date is printed on the bill, drives the list date
  // filters / revenue reports and dates the accrual ledger entry. Choosing it
  // is an explicit per-role capability (`billing:invoice:backdate`); roles
  // without it never send a date and the server stamps "now" on save.
  const { hasPermission } = usePermissions();
  const canBackdate = hasPermission('billing:invoice:backdate');
  const [invoiceDate, setInvoiceDate] = useState(() => toDatetimeLocalValueInTimeZone(new Date()));
  const [invoiceDateError, setInvoiceDateError] = useState<string | undefined>();
  const [paymentDate, setPaymentDate] = useState(() => toDatetimeLocalValueInTimeZone(new Date()));

  // Hospital timezone configured on Settings → General (`hospital_timezone`).
  // Read from the public settings endpoint so counter staff without settings
  // access still seed and submit in the hospital's zone, not the browser's.
  const { timezone: hospitalTimezone, isReady: timezoneReady } = useHospitalTimezone();

  // Seed the date fields once, in the hospital's zone. The guard stops a later
  // settings refetch from overwriting a date the counter picked deliberately.
  const seededDatesRef = useRef(false);
  useEffect(() => {
    if (!timezoneReady || seededDatesRef.current) return;
    seededDatesRef.current = true;
    const now = toDatetimeLocalValueInTimeZone(new Date(), hospitalTimezone);
    setInvoiceDate(now);
    setPaymentDate(now);
  }, [timezoneReady, hospitalTimezone]);

  /** Keep the payment date following the bill date until it is edited by hand. */
  const handleInvoiceDateChange = (value: string) => {
    setInvoiceDate(value);
    if (value) setInvoiceDateError(undefined);
    setPaymentDate((prev) => (prev === invoiceDate ? value : prev));
  };

  const { data: unbilledData, isLoading: unbilledLoading } = useUnbilledCharges(visitId || undefined);
  const unbilledCharges = useMemo(() => unbilledData?.data || [], [unbilledData]);

  // Ensure every loaded charge gets a discount entry so its row is controlled.
  useEffect(() => {
    if (unbilledCharges.length === 0) return;
    setDiscounts((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const charge of unbilledCharges) {
        if (!next[charge.id]) {
          next[charge.id] = { value: '', type: 'PERCENT' };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [unbilledCharges]);

  const selectedCharges = useMemo(
    () => unbilledCharges.filter((c: any) => selectedChargeIds.includes(c.id)),
    [unbilledCharges, selectedChargeIds],
  );

  const totals = useMemo(() => {
    let subtotal = 0;
    let tax = 0;
    let serviceDiscount = 0;
    for (const charge of selectedCharges) {
      const base = Number(charge.amount || 0);
      const entry = discounts[charge.id];
      subtotal += base;
      tax += Number(charge.tax || 0);
      if (entry) serviceDiscount += resolveDiscountAmount(base, entry.value, entry.type);
    }

    const round = (n: number) => Math.round(n * 100) / 100;
    const subtotalR = round(subtotal);
    const taxR = round(tax);
    const serviceDiscountR = round(serviceDiscount);
    const remaining = round(subtotalR + taxR - serviceDiscountR);

    // Invoice-level discount, applied on top of the per-service discounts. A
    // percentage applies to the gross subtotal (matching the backend), a flat
    // amount is capped so the bill can never go below zero.
    const invoiceNum = parseFloat(invoiceDiscount.value);
    let invoiceDiscountAmount = 0;
    if (invoiceDiscount.value && Number.isFinite(invoiceNum) && invoiceNum > 0) {
      invoiceDiscountAmount =
        invoiceDiscount.type === 'PERCENT'
          ? round((subtotalR * Math.min(invoiceNum, 100)) / 100)
          : round(invoiceNum);
    }
    const appliedInvoiceDiscount = round(Math.min(invoiceDiscountAmount, Math.max(remaining, 0)));
    const totalDiscount = round(serviceDiscountR + appliedInvoiceDiscount);

    return {
      count: selectedCharges.length,
      subtotal: subtotalR,
      tax: taxR,
      serviceDiscount: serviceDiscountR,
      invoiceDiscount: appliedInvoiceDiscount,
      discount: totalDiscount,
      remaining: Math.max(remaining, 0),
      total: round(Math.max(remaining - appliedInvoiceDiscount, 0)),
    };
  }, [selectedCharges, discounts, invoiceDiscount]);

  // Optional payment taken at creation time. Capped to the invoice total so the
  // backend's "amount exceeds due" guard can never trip.
  const paymentValue = useMemo(() => {
    const num = parseFloat(paymentAmount);
    if (!Number.isFinite(num) || num <= 0) return 0;
    return round2(Math.min(num, totals.total));
  }, [paymentAmount, totals.total]);
  const balanceAfterPayment = round2(Math.max(totals.total - paymentValue, 0));

  const updatePaymentAmount = (raw: string) => {
    setPaymentAmount(sanitizeDecimalInput(raw));
  };

  const allSelected = unbilledCharges.length > 0 && selectedChargeIds.length === unbilledCharges.length;

  const toggleCharge = (id: string) => {
    setSelectedChargeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAll = () => {
    setSelectedChargeIds(allSelected ? [] : unbilledCharges.map((c: any) => c.id));
  };

  const updateDiscount = (id: string, raw: string, base: number) => {
    const sanitized = sanitizeDecimalInput(raw);
    setDiscounts((prev) => {
      const type = prev[id]?.type || 'PERCENT';
      let value = sanitized;
      // Clamp so an individual line can never exceed its own amount.
      const num = parseFloat(value);
      if (Number.isFinite(num)) {
        const max = type === 'PERCENT' ? 100 : base;
        if (num > max) value = String(max);
      }
      return { ...prev, [id]: { value, type } };
    });
  };

  const setDiscountType = (id: string, type: DiscountType, base: number) => {
    setDiscounts((prev) => {
      const current = prev[id] || { value: '', type: 'PERCENT' };
      let value = current.value;
      const num = parseFloat(value);
      if (Number.isFinite(num)) {
        const max = type === 'PERCENT' ? 100 : base;
        if (num > max) value = String(max);
      }
      return { ...prev, [id]: { value, type } };
    });
  };

  const updateInvoiceDiscount = (raw: string) => {
    const sanitized = sanitizeDecimalInput(raw);
    setInvoiceDiscount((prev) => {
      let value = sanitized;
      const num = parseFloat(value);
      if (Number.isFinite(num)) {
        const max = prev.type === 'PERCENT' ? 100 : totals.remaining;
        if (num > max) value = String(max);
      }
      return { ...prev, value };
    });
  };

  const setInvoiceDiscountType = (type: DiscountType) => {
    setInvoiceDiscount((prev) => {
      let value = prev.value;
      const num = parseFloat(value);
      if (type === 'PERCENT' && Number.isFinite(num) && num > 100) value = '100';
      return { ...prev, type, value };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!patientId || !visitId) {
      setFormError('Please select a patient and an active visit.');
      return;
    }
    if (selectedChargeIds.length === 0) {
      setFormError('Select at least one service to include in the invoice.');
      return;
    }
    if (canBackdate && !invoiceDate) {
      setInvoiceDateError('Invoice date & time is required');
      setFormError('Please choose the invoice date & time');
      return;
    }
    setInvoiceDateError(undefined);

    const chargeDiscounts = selectedCharges
      .map((charge: any) => {
        const entry = discounts[charge.id];
        const value = parseFloat(entry?.value || '');
        if (!entry || !Number.isFinite(value) || value <= 0) return null;
        return {
          chargeEventId: charge.id,
          discountType: entry.type,
          discountValue: value,
        };
      })
      .filter(Boolean);

    // Invoice-level discount (on top of per-service discounts). A percent is
    // sent as `discountPercent`, a flat amount as `discount`. Both are clamped
    // so the combined discount can never exceed the bill.
    const invoiceDiscountValue = parseFloat(invoiceDiscount.value);
    const hasInvoiceDiscount =
      Boolean(invoiceDiscount.value) &&
      Number.isFinite(invoiceDiscountValue) &&
      invoiceDiscountValue > 0;

    const round2 = (n: number) => Math.round(n * 100) / 100;
    const safePercent =
      totals.subtotal > 0
        ? Math.min(invoiceDiscountValue, round2((totals.remaining / totals.subtotal) * 100))
        : 0;

    try {
      const res = await createInvoice.mutateAsync({
        patientId,
        visitId,
        chargeEventIds: selectedChargeIds,
        chargeDiscounts: chargeDiscounts as any,
        notes: notes.trim() || undefined,
        issuedById: user?.id,
        // Omitted for roles without the backdate permission: the server records
        // the "now" it stamps itself.
        ...(canBackdate && invoiceDate
          ? { invoiceDate: datetimeLocalToISOInTimeZone(invoiceDate, hospitalTimezone) }
          : {}),
        ...(hasInvoiceDiscount
          ? invoiceDiscount.type === 'PERCENT'
            ? { discountPercent: safePercent }
            : { discount: Math.min(invoiceDiscountValue, totals.remaining) }
          : {}),
      });
      const created = (res as any)?.data;
      let paidNow = Number(created?.paidAmount || 0);
      const totalNow = Number(created?.total || 0);

      // Record the optional payment taken at creation time. The invoice already
      // exists, so a payment failure must not lose it — surface a toast instead.
      if (created?.id && paymentValue > 0) {
        const amount = Math.min(paymentValue, totalNow || paymentValue);
        try {
          await recordPayment.mutateAsync({
            invoiceId: created.id,
            amount,
            paymentMode,
            referenceNumber: paymentReference.trim() || undefined,
            receivedBy: user?.id,
            // Money received on a backdated bill is posted on the date the
            // counter chose, so the cashbook and ledger agree with the bill.
            ...(canBackdate && paymentDate
              ? { paymentDate: datetimeLocalToISOInTimeZone(paymentDate, hospitalTimezone) }
              : {}),
          });
          paidNow = round2(paidNow + amount);
        } catch (payErr: any) {
          toast.error(
            `Invoice created, but recording the payment failed: ${payErr?.message || 'unknown error'}`,
          );
        }
      }

      setCreatedInvoice({
        ...(created || {}),
        paidAmount: paidNow,
        dueAmount: round2(Math.max(totalNow - paidNow, 0)),
      });
      setShowSuccess(true);
    } catch (err: any) {
      setFormError(err?.message || 'Failed to create invoice');
    }
  };

  const handlePrintInvoice = async () => {
    const id = createdInvoice?.id;
    if (!id) {
      navigate('/billing/invoices');
      return;
    }
    setPrintBusy(true);
    try {
      const url = await billingService.fetchInvoicePdfUrl(id);
      await printInvoicePdf(url);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to print invoice');
    } finally {
      setPrintBusy(false);
      setShowSuccess(false);
      navigate('/billing/invoices');
    }
  };

  const handleGoToList = () => {
    setShowSuccess(false);
    navigate('/billing/invoices');
  };

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="space-y-3">
        <nav className="flex items-center gap-1.5 text-xs text-text-muted">
          <Link to="/billing/invoices" className="hover:text-primary transition-colors">
            Invoices
          </Link>
          <ChevronRight size={13} />
          <span className="text-text">New Invoice</span>
        </nav>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => navigate('/billing/invoices')}
              className="p-2 rounded-lg border border-border bg-surface text-text-muted hover:text-text hover:bg-background transition-colors"
              title="Back to invoices"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <Receipt className="w-6 h-6 text-primary" />
                <h1 className="text-2xl font-bold text-text">Create Invoice</h1>
                <PageHelp path="/billing/invoices" />
              </div>
              <p className="text-sm text-text-muted mt-1">
                Build an invoice from a visit's unbilled services. Apply a percentage or a flat
                discount to each service individually.
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-primary/10 text-primary">
            <Plus size={13} />
            New Invoice
          </span>
        </div>
      </div>

      {formError && (
        <div className="flex items-start gap-2 p-3.5 bg-danger/10 border border-danger/20 rounded-xl text-sm text-danger">
          <AlertCircle size={17} className="shrink-0 mt-0.5" />
          <span>{formError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
          {/* Left: form */}
          <div className="xl:col-span-2 space-y-6">
            {/* Patient & visit */}
            <section className="bg-surface border border-border rounded-2xl shadow-sm">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
                <Wallet size={17} className="text-primary" />
                <h2 className="text-sm font-bold text-text uppercase tracking-wide">Patient &amp; Visit</h2>
              </div>
              <div className="p-5">
                <PatientVisitSelector
                  patientId={patientId}
                  visitId={visitId}
                  onChange={({ patientId: nextPatientId, visitId: nextVisitId }) => {
                    setPatientId(nextPatientId);
                    setVisitId(nextVisitId);
                    setSelectedChargeIds([]);
                    setFormError(null);
                  }}
                />
              </div>
            </section>

            {/* Services */}
            <section className="bg-surface border border-border rounded-2xl shadow-sm">
              <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border flex-wrap">
                <div className="flex items-center gap-2">
                  <Tag size={17} className="text-primary" />
                  <h2 className="text-sm font-bold text-text uppercase tracking-wide">
                    Services &amp; Charges
                  </h2>
                  {unbilledLoading && <Loader2 size={14} className="animate-spin text-text-muted" />}
                </div>
                {unbilledCharges.length > 0 && (
                  <span className="text-xs text-text-muted">
                    {selectedChargeIds.length} of {unbilledCharges.length} selected
                  </span>
                )}
              </div>

              <div className="p-5">
                {!visitId ? (
                  <div className="flex flex-col items-center justify-center text-center py-10 gap-2">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Info size={20} className="text-primary" />
                    </div>
                    <p className="text-sm font-medium text-text">Select a patient and visit</p>
                    <p className="text-xs text-text-muted max-w-sm">
                      Unbilled services will appear here once an active visit is selected.
                    </p>
                  </div>
                ) : unbilledLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : unbilledCharges.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-center py-10 gap-2">
                    <div className="p-3 rounded-full bg-success/10">
                      <CheckCircle2 size={20} className="text-success" />
                    </div>
                    <p className="text-sm font-medium text-text">All charges are billed</p>
                    <p className="text-xs text-text-muted max-w-sm">
                      There are no unbilled services for this visit.
                    </p>
                  </div>
                ) : (
                  <div className="border border-border rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[720px]">
                        <thead>
                          <tr className="border-b border-border bg-background/60">
                            <th className="px-4 py-3 text-left w-10">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={toggleAll}
                                className="rounded border-border"
                                title="Select all services"
                              />
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wide">
                              Service
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wide">
                              Amount
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wide">
                              Tax
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wide">
                              Discount
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wide">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {unbilledCharges.map((charge: any) => {
                            const base = Number(charge.amount || 0);
                            const entry = discounts[charge.id] || { value: '', type: 'PERCENT' as DiscountType };
                            const selected = selectedChargeIds.includes(charge.id);
                            const lineDiscount = resolveDiscountAmount(base, entry.value, entry.type);
                            const lineTotal = base + Number(charge.tax || 0) - lineDiscount;
                            return (
                              <tr
                                key={charge.id}
                                className={selected ? 'bg-primary/[0.03]' : 'hover:bg-background/40'}
                              >
                                <td className="px-4 py-3 align-top">
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={() => toggleCharge(charge.id)}
                                    className="rounded border-border mt-1"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <p className="text-sm font-medium text-text">{charge.chargeDescription}</p>
                                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                    {charge.type && (
                                      <span className="text-[11px] uppercase tracking-wide text-text-muted">
                                        {charge.type}
                                      </span>
                                    )}
                                    {charge.service?.name && charge.service.name !== charge.chargeDescription && (
                                      <span className="text-xs text-text-muted">{charge.service.name}</span>
                                    )}
                                    {charge.unitLabel && Number(charge.quantity) !== 1 && (
                                      <span className="text-[11px] text-text-muted">
                                        {formatQuantity(charge.quantity, charge.unitLabel)}
                                        {charge.unitPrice != null
                                          ? ` × ${money(Number(charge.unitPrice))}`
                                          : ''}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-text text-right align-top whitespace-nowrap">
                                  {money(base)}
                                </td>
                                <td className="px-4 py-3 text-sm text-text text-right align-top whitespace-nowrap">
                                  {money(Number(charge.tax || 0))}
                                </td>
                                <td className="px-4 py-3 align-top">
                                  <div className="flex flex-col items-center gap-1">
                                    <div className="flex items-stretch rounded-lg border border-border overflow-hidden bg-background">
                                      <button
                                        type="button"
                                        onClick={() => setDiscountType(charge.id, 'PERCENT', base)}
                                        disabled={!selected}
                                        className={`px-2 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                          entry.type === 'PERCENT'
                                            ? 'bg-primary text-white'
                                            : 'text-text-muted hover:text-text'
                                        }`}
                                        title="Percentage discount"
                                      >
                                        %
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setDiscountType(charge.id, 'FLAT', base)}
                                        disabled={!selected}
                                        className={`px-2 py-1.5 text-xs font-semibold border-l border-border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                          entry.type === 'FLAT'
                                            ? 'bg-primary text-white'
                                            : 'text-text-muted hover:text-text'
                                        }`}
                                        title="Flat amount discount"
                                      >
                                        ৳
                                      </button>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0"
                                        value={entry.value}
                                        onChange={(e) => updateDiscount(charge.id, e.target.value, base)}
                                        disabled={!selected}
                                        className="w-16 px-2 py-1.5 text-right text-sm bg-background text-text focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                                      />
                                    </div>
                                    {lineDiscount > 0 && (
                                      <span className="text-[11px] font-medium text-danger">
                                        −{money(lineDiscount)}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm font-semibold text-text text-right align-top whitespace-nowrap">
                                  {money(lineTotal)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Notes */}
            <section className="bg-surface border border-border rounded-2xl shadow-sm">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
                <StickyNote size={17} className="text-primary" />
                <h2 className="text-sm font-bold text-text uppercase tracking-wide">Notes</h2>
              </div>
              <div className="p-5">
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes that will appear on the invoice…"
                  className={`${inputClass} resize-none`}
                />
              </div>
            </section>

          </div>
          <div className="xl:col-span-1 space-y-6 xl:sticky xl:top-6">
            <section className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-border bg-background/40">
                <CircleDollarSign size={17} className="text-primary" />
                <h2 className="text-sm font-bold text-text uppercase tracking-wide">Invoice Summary</h2>
              </div>
              <div className="p-5 space-y-3">
                {/* Invoice date & time — the bill's business date. */}
                <div>
                  <label className="mb-1 flex items-center gap-1 text-xs font-medium text-text-muted uppercase">
                    Invoice Date &amp; Time *
                    <InfoHint
                      label="Invoice Date & Time"
                      en="The date this bill is posted on. It is printed on the bill and drives the date filters, reports and ledger. Changing it (backdating) requires the “Invoice Date & Time (Backdate)” permission; otherwise the bill is dated when you save it."
                      bn="এই বিল যে তারিখে পোস্ট হয়েছে। বিলে এটি মুদ্রিত হয় এবং ডেট ফিল্টার, রিপোর্ট ও লেজারে ব্যবহৃত হয়। পরিবর্তন (ব্যাকডেট) করতে “Invoice Date & Time (Backdate)” পারমিশন প্রয়োজন; না থাকলে সংরক্ষণের সময় তারিখ বসবে।"
                    />
                  </label>
                  <DateTimePicker
                    value={invoiceDate}
                    onChange={handleInvoiceDateChange}
                    error={invoiceDateError}
                    timeZone={hospitalTimezone}
                    disabled={!canBackdate}
                    clearable={canBackdate}
                    required
                  />
                  <p className="mt-1 flex items-center gap-1 text-[10px] leading-4 text-text-muted">
                    <Globe size={10} className="shrink-0" />
                    {canBackdate
                      ? `Times shown in ${hospitalTimezone} (Settings → General)`
                      : 'Recorded automatically when you save. Requires the “Invoice Date & Time (Backdate)” permission to change.'}
                  </p>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Selected services</span>
                  <span className="text-text font-medium">{totals.count}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Subtotal</span>
                  <span className="text-text">{money(totals.subtotal)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Tax</span>
                  <span className="text-text">{money(totals.tax)}</span>
                </div>
                {totals.serviceDiscount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">Service discounts</span>
                    <span className="text-danger font-medium">−{money(totals.serviceDiscount)}</span>
                  </div>
                )}

                <div className="border-t border-border pt-3 mt-1 space-y-2">
                  {totals.discount > 0 && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-text-muted">Total discount</span>
                      <span className="text-danger font-medium">−{money(totals.discount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-text">Total payable</span>
                    <span className="text-xl font-bold text-primary">{money(totals.total)}</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Invoice discount */}
            <section className="bg-surface border border-border rounded-2xl shadow-sm">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
                <BadgePercent size={17} className="text-primary" />
                <h2 className="text-sm font-bold text-text uppercase tracking-wide">Invoice Discount</h2>
              </div>
              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Applied</span>
                  <span
                    className={
                      totals.invoiceDiscount > 0 ? 'text-danger font-medium' : 'text-text-muted'
                    }
                  >
                    −{money(totals.invoiceDiscount)}
                  </span>
                </div>
                <div className="flex items-stretch rounded-lg border border-border overflow-hidden bg-surface">
                  <button
                    type="button"
                    onClick={() => setInvoiceDiscountType('PERCENT')}
                    className={`px-3 py-2 text-xs font-semibold transition-colors ${
                      invoiceDiscount.type === 'PERCENT'
                        ? 'bg-primary text-white'
                        : 'text-text-muted hover:text-text'
                    }`}
                    title="Percentage discount"
                  >
                    %
                  </button>
                  <button
                    type="button"
                    onClick={() => setInvoiceDiscountType('FLAT')}
                    className={`px-3 py-2 text-xs font-semibold border-l border-border transition-colors ${
                      invoiceDiscount.type === 'FLAT'
                        ? 'bg-primary text-white'
                        : 'text-text-muted hover:text-text'
                    }`}
                    title="Flat amount discount"
                  >
                    ৳
                  </button>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={invoiceDiscount.value}
                    onChange={(e) => updateInvoiceDiscount(e.target.value)}
                    className="flex-1 min-w-0 px-3 py-2 text-right text-sm bg-surface text-text focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <p className="text-[11px] text-text-muted leading-relaxed">
                  {invoiceDiscount.type === 'PERCENT'
                    ? 'Percentage applied to the subtotal, on top of service discounts.'
                    : 'Flat amount subtracted after service discounts.'}
                </p>
              </div>
            </section>

            <InvoicePaymentCard
              amount={paymentAmount}
              onAmountChange={updatePaymentAmount}
              mode={paymentMode}
              onModeChange={setPaymentMode}
              reference={paymentReference}
              onReferenceChange={setPaymentReference}
              maxAmount={totals.total}
              collecting={paymentValue}
              balanceAfter={balanceAfterPayment}
              disabled={totals.count === 0}
              fullLabel="Full amount"
              paymentDate={canBackdate ? paymentDate : undefined}
              onPaymentDateChange={canBackdate ? setPaymentDate : undefined}
              timeZone={hospitalTimezone}
            />

            <div className="flex items-start gap-2.5 p-4 bg-primary/5 border border-primary/20 rounded-2xl">
              <Info size={16} className="text-primary shrink-0 mt-0.5" />
              <p className="text-xs text-text-muted leading-relaxed">
                Toggle <span className="font-semibold text-text">%</span> for a percentage or{' '}
                <span className="font-semibold text-text">৳</span> for a flat amount — discount each
                service in the table, then apply an overall invoice discount below.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/billing/invoices')}
                className="px-4 py-2.5 text-sm font-medium text-text-muted hover:text-text border border-border rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createInvoice.isPending || selectedChargeIds.length === 0}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createInvoice.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Plus size={16} />
                )}
                Create Invoice
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Success modal: print or go to the list */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-surface rounded-2xl border border-border p-6 w-full max-w-sm space-y-5 shadow-xl">
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                <CheckCircle2 size={24} className="text-success" />
              </div>
              <h3 className="text-lg font-bold text-text">Invoice Created!</h3>
              <p className="text-sm text-text-muted">
                Invoice{' '}
                <span className="font-mono font-medium text-text">
                  {createdInvoice?.invoiceNumber || ''}
                </span>{' '}
                has been created successfully.
              </p>
            </div>
            {Number(createdInvoice?.total || 0) > 0 && (
              <div className="rounded-xl border border-border bg-background/60 p-3 space-y-1.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Total</span>
                  <span className="text-text">{money(Number(createdInvoice.total || 0))}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Paid</span>
                  <span className="text-success">{money(Number(createdInvoice.paidAmount || 0))}</span>
                </div>
                <div className="flex items-center justify-between font-medium">
                  <span className="text-text-muted">Due</span>
                  <span
                    className={
                      Number(createdInvoice.dueAmount || 0) > 0 ? 'text-danger' : 'text-success'
                    }
                  >
                    {money(Number(createdInvoice.dueAmount || 0))}
                  </span>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handlePrintInvoice}
                disabled={printBusy}
                className="px-4 py-2.5 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {printBusy ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                Print
              </button>
              <button
                type="button"
                onClick={handleGoToList}
                className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                <ListChecks size={16} />
                Go to List
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
